/**
 * The shell: four stable destinations (Today, Cattle, Work, Ranch), the Hondo bar everywhere, and
 * pinned workspaces. Phones get a bottom bar with a raised record button; desktops get a rail.
 * It also keeps the field basics honest: what's saved on this device, whether we're offline, and
 * where Back returns to.
 */
import { Suspense, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate, useNavigationType } from "react-router";
import { api } from "../lib/api";
import { plural, t, useLanguage } from "../lib/i18n";
import { clearSnapshots, pruneSnapshots, useConnection } from "../lib/offline";
import { discardSaved, flush, startOutbox, useOutbox } from "../lib/outbox";
import { invalidateAll, queryClient, useApi } from "../lib/query";
import { useMe } from "../lib/session";
import { formatDate, timeAgo } from "../lib/format";
import { registry } from "../registry";
import { RecordSheetHost } from "../forms/EventForm";
import { Avatar, Button, Chip, Empty, Icon, IconButton, Loading, Menu, Sheet, Toaster, cx, isSheetOpen, toast, useIsWide } from "../ui/kit";
import { Composer, ComposerStatus, RecordTabButton, useComposer, useComposerMic } from "./Composer";

const DESTINATIONS = [
  { to: "/", label: "Today", icon: "today" },
  { to: "/cattle", label: "Cattle", icon: "cattle" },
  { to: "/work", label: "Work", icon: "work" },
  { to: "/ranch", label: "Ranch", icon: "ranch" },
] as const;

/** Which of the four destinations a page belongs to, so the right tab stays lit on deep pages. */
export function destinationFor(pathname: string): string | null {
  if (pathname === "/" || pathname.startsWith("/review") || pathname.startsWith("/history")) return "/";
  if (pathname.startsWith("/cattle")) return "/cattle";
  if (pathname.startsWith("/work")) return "/work";
  if (pathname.startsWith("/ranch") || pathname.startsWith("/settings")) return "/ranch";
  return null;
}

function Brand({ ranch }: { ranch: string }) {
  return (
    <Link to="/" className="brand" aria-label={t("Hondo, {{ranch}}. Go to Today", { ranch })}>
      <img src="/favicon.svg" alt="" width={32} height={32} />
      <span className="brand-text"><span className="brand-name">Hondo</span><span className="brand-ranch">{ranch}</span></span>
    </Link>
  );
}

export interface Pin { id: string; label: string; icon: string; to: string }

/** Pinned workspaces: the person's own, else the ranch's, else what the ranch's presets suggest. */
const PRESET_PINS: Record<string, string[]> = {
  basic: ["calving", "health", "grazing"],
  seedstock: ["breeding", "registration", "sales"],
  commercial: ["calving", "health", "sales"],
  feedlot: ["feeding", "health", "sessions"],
  dairy: ["dairy", "health", "breeding"],
};

export function usePins(): { pins: Pin[]; ids: string[]; personal: boolean; save: (ids: string[]) => Promise<void> } {
  const { data } = useMe();
  const personal = data?.membership?.pins ?? [];
  const ranch = data?.ranch?.pins ?? [];
  // Until someone pins their own, a few workspaces per starting preset (the product map's profiles), not every match.
  const presetPins = [...new Set((data?.ranch?.presets ?? []).flatMap((p) => PRESET_PINS[p] ?? []))]
    .filter((id) => registry.workspaces.some((w) => w.id === id)).slice(0, 5);
  const ids = personal.length ? personal : ranch.length ? ranch : presetPins;
  const pins = ids.map((id) => registry.workspaces.find((w) => w.id === id)).filter((w): w is NonNullable<typeof w> => !!w).slice(0, 12)
    .map((w) => ({ id: w.id, label: w.label, icon: w.icon, to: `/work/${w.id}` }));
  const save = async (next: string[]) => { await api.patch("/me", { pins: next }); await invalidateAll(); };
  return { pins, ids: pins.map((p) => p.id), personal: personal.length > 0, save };
}

/** Back returns to where you were, scrolled where you left it. New pages start at the top. */
function useScrollRestoration() {
  const location = useLocation();
  const navType = useNavigationType();
  const positions = useRef<Map<string, number>>(new Map());
  const previous = useRef(location);
  useEffect(() => {
    try { history.scrollRestoration = "manual"; } catch { /* old browsers */ }
    try { positions.current = new Map(JSON.parse(sessionStorage.getItem("hondo.v2.scroll") ?? "[]")); } catch { /* optional */ }
  }, []);
  useEffect(() => {
    let frame = 0;
    const save = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        positions.current.set(location.key, window.scrollY);
        if (positions.current.size > 60) positions.current.delete(positions.current.keys().next().value!);
        try { sessionStorage.setItem("hondo.v2.scroll", JSON.stringify([...positions.current])); } catch { /* optional */ }
      });
    };
    window.addEventListener("scroll", save, { passive: true });
    return () => { cancelAnimationFrame(frame); window.removeEventListener("scroll", save); };
  }, [location.key]);
  useLayoutEffect(() => {
    const prev = previous.current;
    previous.current = location;
    if (prev.key === location.key) return;
    // Filters and tabs replace the address without leaving the page: stay put.
    if (navType === "REPLACE" && prev.pathname === location.pathname) return;
    const saved = navType === "POP" ? positions.current.get(location.key) : undefined;
    if (saved === undefined) {
      if (location.hash) document.getElementById(decodeURIComponent(location.hash.slice(1)))?.scrollIntoView();
      else window.scrollTo(0, 0);
      return;
    }
    // Data may still be loading: keep trying briefly until the page is tall enough.
    const started = performance.now();
    let frame = 0;
    const attempt = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max >= saved || performance.now() - started > 1500) { window.scrollTo(0, Math.min(saved, Math.max(max, 0))); return; }
      frame = requestAnimationFrame(attempt);
    };
    attempt();
    return () => cancelAnimationFrame(frame);
  }, [location, navType]);
}

export function AppShell() {
  useLanguage();
  const { data: me, isLoading, error } = useMe();
  const wide = useIsWide();
  const { state, set } = useComposer();
  const mic = useComposerMic();
  const location = useLocation();
  const navigate = useNavigate();
  const { pins } = usePins();
  const outbox = useOutbox();
  const [outboxOpen, setOutboxOpen] = useState(false);
  const [pinsOpen, setPinsOpen] = useState(false);
  const review = useApi<{ items: unknown[] }>(me?.ranch ? "/captures?status=needs_review&limit=50" : null, { refetchInterval: 30_000 });
  const reviewCount = review.data?.items.length ?? 0;
  const here = destinationFor(location.pathname);
  useScrollRestoration();

  useEffect(() => { if (me?.ranch && me.membership) void startOutbox(me.ranch.id, me.membership.id); void pruneSnapshots(); }, [me?.ranch?.id, me?.membership?.id]);
  // Moving to another page puts the phone sheet away (the desktop panel stays until dismissed).
  useEffect(() => { if (!wide && state.open && !mic.holding.active && mic.mode !== "recording") set({ open: false }); }, [location.pathname]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement | null)?.closest?.("input, textarea, select, [contenteditable='true']");
      const shortcut = ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") || (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey && !e.altKey);
      if (!shortcut || (isSheetOpen() && wide)) return;
      e.preventDefault();
      set({ open: true, seq: (state.seq ?? 0) + 1, draft: "", focus: "text" });
      if (wide) requestAnimationFrame(() => document.querySelector<HTMLTextAreaElement>(".composer-bar .composer-input")?.focus());
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [set, state.seq, wide]);

  if (isLoading) return <Loading />;
  if (!me && error) return <OfflineStart />;
  if (!me) return <Navigate to="/signin" replace state={{ from: location.pathname }} />;
  if (!me.ranch) return <Navigate to="/start" replace />;

  const signOut = async () => {
    try { await api.post("/auth/signout"); } catch { /* still forget this device's copy */ }
    queryClient.clear();
    await clearSnapshots();
    navigate("/signin");
  };
  const switchRanch = async (id: string) => { await api.post("/me/ranch", { ranch_id: id }); queryClient.clear(); navigate("/"); };
  const accountMenu = (
    <Menu label={t("Account")} items={[
      { label: t("Settings"), icon: "settings", to: "/settings" },
      { label: t("Your data"), icon: "data", to: "/ranch/data" },
      ...me.ranches.filter((r) => r.id !== me.ranch!.id).map((r) => ({ label: t("Switch to {{ranch}}", { ranch: r.name }), icon: "ranch", onSelect: () => void switchRanch(r.id) })),
      "divider" as const,
      { label: t("Sign out"), icon: "logout", onSelect: () => void signOut() },
    ]} />
  );
  const sheetFocus = state.draft || state.focus === "text" ? ".composer-sheet .composer-input" : ".composer-sheet .mic-main";

  return (
    <div className={cx("shell", wide ? "shell-wide" : "shell-narrow")}>
      <a href="#main" className="skip-link" onClick={(e) => { e.preventDefault(); document.getElementById("main")?.focus(); }}>{t("Skip to content")}</a>
      {wide ? (
        <aside className="rail" aria-label={t("Main")}>
          <Brand ranch={me.ranch.name} />
          <nav className="rail-nav" aria-label={t("Main")}>
            {DESTINATIONS.map((d) => (
              <Link key={d.to} to={d.to} aria-current={here === d.to ? "page" : undefined} className={cx("rail-link", here === d.to && "active")}>
                <Icon name={d.icon} size={20} /><span>{t(d.label)}</span>
                {d.to === "/" && reviewCount > 0 && <span className="nav-badge" aria-label={t("{{count}} need a look", { count: reviewCount })}>{reviewCount}</span>}
              </Link>
            ))}
          </nav>
          <nav className="rail-pins" aria-label={t("Pinned")}>
            <div className="rail-heading-row"><p className="rail-heading">{t("Pinned")}</p>{pins.length > 0 && <button type="button" className="link small" onClick={() => setPinsOpen(true)}>{t("Edit")}</button>}</div>
            {pins.map((p) => {
              const active = location.pathname === p.to || location.pathname.startsWith(`${p.to}/`);
              return <Link key={p.id} to={p.to} aria-current={active ? "page" : undefined} className={cx("rail-link", "rail-pin", active && "active")}><Icon name={p.icon} size={18} /><span>{t(p.label)}</span></Link>;
            })}
            {pins.length === 0 && <Link to="/work#directory" className="rail-empty">{t("Pin the workspaces you use from Work.")}</Link>}
          </nav>
          <div className="rail-foot">
            {outbox.length > 0 && (
              <button type="button" className="sync-note" onClick={() => setOutboxOpen(true)}>
                <Icon name={outbox.some((o) => o.state === "sending") ? "upload" : "offline"} size={16} />
                {plural(outbox.length, "{{count}} note saved on this device", "{{count}} notes saved on this device")}
              </button>
            )}
            <div className="rail-me"><Avatar name={me.person.name} /><span>{me.person.name}</span>{accountMenu}</div>
          </div>
        </aside>
      ) : (
        <header className="topbar">
          <Brand ranch={me.ranch.name} />
          <div className="topbar-actions">
            <ComposerStatus />
            {outbox.length > 0 && (
              <button type="button" className="icon-btn sync-dot" aria-label={plural(outbox.length, "{{count}} note saved on this device", "{{count}} notes saved on this device")} onClick={() => setOutboxOpen(true)}>
                <Icon name="offline" size={20} /><span className="sync-count">{outbox.length}</span>
              </button>
            )}
            <button type="button" className="icon-btn" aria-label={t("Search or tell Hondo")} onClick={() => set({ open: true, draft: "", seq: (state.seq ?? 0) + 1, focus: "text" })}><Icon name="search" size={22} /></button>
            {accountMenu}
          </div>
        </header>
      )}

      <div className="main-col">
        {wide && <div className="bar-wrap"><Composer variant="bar" /></div>}
        <OfflineBanner />
        <main className="content" id="main" tabIndex={-1}><Suspense fallback={<Loading />}><Outlet /></Suspense></main>
      </div>

      {!wide && (
        <nav className="tabbar" aria-label={t("Main")}>
          {DESTINATIONS.slice(0, 2).map((d) => <TabLink key={d.to} {...d} active={here === d.to} badge={d.to === "/" ? reviewCount : 0} />)}
          <RecordTabButton />
          {DESTINATIONS.slice(2).map((d) => <TabLink key={d.to} {...d} active={here === d.to} badge={0} />)}
        </nav>
      )}
      {!wide && (
        <Sheet open={state.open} onClose={() => { if (mic.mode === "recording") mic.stop(); set({ open: false }); }} title={t("Tell Hondo")} size="full" initialFocus={sheetFocus} className="composer-dialog">
          <Composer variant="sheet" onNavigate={() => set({ open: false })} />
        </Sheet>
      )}
      <OutboxSheet open={outboxOpen} onClose={() => setOutboxOpen(false)} />
      <PinsSheet open={pinsOpen} onClose={() => setPinsOpen(false)} />
      <RecordSheetHost />
      <Toaster />
    </div>
  );
}

function TabLink({ to, label, icon, badge, active }: { to: string; label: string; icon: string; badge: number; active: boolean }) {
  return (
    <Link to={to} aria-current={active ? "page" : undefined} className={cx("tab-link", active && "active")}>
      <span className="tab-icon"><Icon name={icon} size={22} />{badge > 0 && <span className="nav-badge" aria-label={t("{{count}} need a look", { count: badge })}>{badge}</span>}</span>
      <span>{t(label)}</span>
    </Link>
  );
}

export function Page({ children, wide }: { children: ReactNode; wide?: boolean }) {
  return <div className={cx("page", wide && "page-wide")}>{children}</div>;
}

// ---------------------------------------------------------------- offline

/** Shown above every page while the network is gone, and while saved copies are on screen. */
export function OfflineBanner() {
  const { offline, savedAt } = useConnection();
  const outbox = useOutbox();
  if (!offline && !savedAt) return null;
  return (
    <div className={cx("offline-banner", savedAt ? "is-saved" : "is-offline")} role="status">
      <Icon name="noSignal" size={18} />
      <span>
        {savedAt ? <><strong>{t("Offline — showing saved data")}</strong> {t("from {{when}}. You can look, but changes wait until you're back.", { when: timeAgo(savedAt) })}</>
          : <><strong>{t("You're offline.")}</strong> {t("Notes you record are saved here and send when you're back.")}</>}
        {outbox.length > 0 && ` ${t("{{count}} waiting to send.", { count: outbox.length })}`}
      </span>
    </div>
  );
}

/** Opening the app with no signal and nothing saved yet on this device. */
function OfflineStart() {
  return (
    <div className="auth">
      <div className="auth-card">
        <div className="auth-brand"><img src="/favicon.svg" alt="" width={48} height={48} /><span>Hondo</span></div>
        <Empty icon="noSignal" title={t("No signal, and nothing saved on this device yet")} body={t("Open Hondo once with signal and it keeps a copy of Today and your cattle for times like this.")}
          action={<Button variant="primary" icon="reset" onClick={() => location.reload()}>{t("Try again")}</Button>} />
      </div>
    </div>
  );
}

/** Notes saved in this browser that haven't reached Hondo yet. */
function OutboxSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const outbox = useOutbox();
  const wide = useIsWide();
  const { offline } = useConnection();
  return (
    <Sheet open={open} onClose={onClose} title={wide ? t("Saved on this device") : t("Saved on this phone")} size="sm">
      {outbox.length === 0 ? <Empty icon="done" title={t("Everything has been sent")} /> : (
        <div className="stack">
          <p className="muted">{offline ? t("These send by themselves when you're back online. Keep Hondo open or come back later.") : t("These are on their way. If one is stuck, try sending it now.")}</p>
          <ul className="list">
            {outbox.map((o) => (
              <li key={o.id} className="outbox-row">
                <Icon name={o.kind === "voice" ? "mic" : o.kind === "photo" ? "camera" : "note"} size={18} />
                <span className="outbox-main">
                  <span className="outbox-text">{o.text ?? (o.kind === "voice" ? t("Voice note") : o.kind === "photo" ? t("Photo") : t("File"))}</span>
                  <span className="muted small">{formatDate(o.created_at, "minute")}{o.error ? ` · ${o.error}` : ""}</span>
                </span>
                <Chip tone={o.state === "sending" ? "info" : "warn"}>{o.state === "sending" ? t("Sending") : wide ? t("Saved on this device") : t("Saved on this phone")}</Chip>
                {o.state !== "sending" && <IconButton icon="delete" label={t("Discard this note")} onClick={() => void discardSaved(o.id).then(() => toast(t("Note discarded.")))} />}
              </li>
            ))}
          </ul>
          <Button icon="upload" onClick={() => void flush()} disabled={offline}>{t("Send now")}</Button>
        </div>
      )}
    </Sheet>
  );
}

/** Reorder or unpin the workspaces in the rail. */
function PinsSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { pins, save } = usePins();
  const [order, setOrder] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setOrder(pins.map((p) => p.id)); }, [open]);
  const byId = new Map(pins.map((p) => [p.id, p]));
  const move = (i: number, by: number) => setOrder((o) => { const n = [...o]; const [x] = n.splice(i, 1); n.splice(Math.max(0, Math.min(n.length, i + by)), 0, x); return n; });
  return (
    <Sheet open={open} onClose={onClose} title={t("Pinned workspaces")} size="sm"
      footer={<div className="row-actions"><Button variant="primary" busy={busy} onClick={async () => { setBusy(true); try { await save(order); onClose(); toast(t("Saved"), { tone: "good" }); } finally { setBusy(false); } }}>{t("Save")}</Button><Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button></div>}>
      {order.length === 0 ? <p className="muted">{t("Nothing pinned. Pin workspaces from the list on Work.")}</p> : (
        <ol className="list pin-list">
          {order.map((id, i) => {
            const p = byId.get(id);
            if (!p) return null;
            return (
              <li key={id} className="pin-row">
                <Icon name={p.icon} size={18} /><span className="pin-name">{t(p.label)}</span>
                <IconButton icon="up" label={t("Move {{name}} up", { name: t(p.label) })} disabled={i === 0} onClick={() => move(i, -1)} />
                <IconButton icon="down" label={t("Move {{name}} down", { name: t(p.label) })} disabled={i === order.length - 1} onClick={() => move(i, 1)} />
                <IconButton icon="unpin" label={t("Unpin {{name}}", { name: t(p.label) })} onClick={() => setOrder((o) => o.filter((x) => x !== id))} />
              </li>
            );
          })}
        </ol>
      )}
      <p className="field-hint">{t("Pins are just shortcuts. Unpinning never hides records or work.")}</p>
    </Sheet>
  );
}
